import { useDebounce } from '@/hooks/use-debounce';
import api from '@/services/api';
import { getApiErrorMessage } from '@/utils/api-error';
import { Ionicons } from '@expo/vector-icons';
import React, { useCallback, useEffect, useState } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  ViewProps,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { ThemedText } from '../themed-text';
import { useThemedHex } from '@/hooks/use-theme-colors';

type CreateClientByProviderDto = {
  username: string;
  email?: string;
  phone: string;
};

type ControlledSearchableSelectProps<T> = {
  control: Control<any>;
  name: string;
  label?: string;
  disabled?: boolean;
  fetchEndpoint: string;
  modalTitle: string;
  initialSelectedName?: string;
  valueAccessor: (item: T) => string;
  displayAccessor: (item: T) => string;
  renderListItem: (item: T) => React.ReactElement;
  allowCreation?: boolean;
  createEndpoint?: string;
  testID?: string;
  /** Posição do modal: 'center' (padrão) ou 'top' (acima do trigger). Padrão: center. */
  dropdownPlacement?: 'top' | 'center';
} & ViewProps;

export function ControlledSearchableSelect<T>({
  control,
  name,
  label,
  disabled = false,
  fetchEndpoint,
  modalTitle,
  initialSelectedName,
  valueAccessor,
  displayAccessor,
  renderListItem,
  allowCreation = false,
  createEndpoint = '',
  testID,
  dropdownPlacement = 'center',
  ...viewProps
}: ControlledSearchableSelectProps<T>) {
  const th = useThemedHex();
  const [modalVisible, setModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [displayName, setDisplayName] = useState(initialSelectedName || '');

  const [viewMode, setViewMode] = useState<'search' | 'create'>('search');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newClientData, setNewClientData] = useState<CreateClientByProviderDto>({
    username: '',
    email: '',
    phone: '',
  });

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const value = useWatch({
    control,
    name,
  });

  useEffect(() => {
    if (initialSelectedName) {
      setDisplayName(initialSelectedName);
    } else if (!value) {
      setDisplayName('');
    }
  }, [value, initialSelectedName]);

  const fetchResults = useCallback(
    async (query: string) => {
      if (query.length < 2) {
        setResults([]);
        return;
      }
      setIsLoading(true);
      try {
        const response = await api.get<any>(`/${fetchEndpoint}`, {
          params: { search: query },
        });
        const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setResults(data);
      } catch (error) {
        console.error('Failed to fetch search results:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchEndpoint],
  );

  useEffect(() => {
    if (viewMode === 'search') {
      fetchResults(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, fetchResults, viewMode]);

  const resetCreateForm = () => {
    setNewClientData({ username: '', email: '', phone: '' });
    setCreateError(null);
    setIsCreating(false);
  };

  const openModal = useCallback(() => {
    if (disabled) return;
    setSearchQuery('');
    setResults([]);
    setViewMode('search');
    resetCreateForm();
    setModalVisible(true);
  }, [disabled]);

  const closeModal = useCallback(() => {
    setModalVisible(false);
  }, []);

  const handleCreateFormChange = (name: keyof CreateClientByProviderDto, text: string) => {
    setNewClientData((prev) => ({ ...prev, [name]: text }));
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur }, fieldState: { error } }) => {
        const handleSelect = (item: T) => {
          onChange(valueAccessor(item));
          setDisplayName(displayAccessor(item));
          onBlur();
          closeModal();
        };

        const handleCreateNewClient = async () => {
          if (!createEndpoint) {
            setCreateError('Não foi possível criar agora. Tente novamente.');
            return;
          }

          if (!newClientData.username?.trim()) {
            setCreateError('Nome é obrigatório.');
            return;
          }
          if (!newClientData.phone?.trim()) {
            setCreateError('Telefone é obrigatório.');
            return;
          }

          setIsCreating(true);
          setCreateError(null);

          try {
            const dto: CreateClientByProviderDto = {
              username: newClientData.username.trim(),
              phone: newClientData.phone.trim(),
              ...(newClientData.email?.trim() && { email: newClientData.email.trim() }),
            };

            const response = await api.post<{ client: T }>(`/${createEndpoint}`, dto);

            const newClient = response.data.client;

            handleSelect(newClient);
          } catch (error: any) {
            console.error('Failed to create client:', error);
            const message = getApiErrorMessage(error, 'Erro ao criar cliente.');
            setCreateError(message);
          } finally {
            setIsCreating(false);
          }
        };

        return (
          <View className="relative mb-4 w-full" {...viewProps}>
            {label && <ThemedText type="labelInput">{label}</ThemedText>}
            <Pressable
              onPress={openModal}
              disabled={disabled}
              testID={testID}
              className={`
                min-h-[48px] flex-row items-center justify-between rounded-xl border bg-card px-4 py-3
                ${error ? 'border-error-500' : 'border-neutral-200'}
                ${disabled ? 'opacity-50' : ''}
              `}
            >
              <Text
                className={`
                  text-base
                  ${displayName ? 'text-foreground' : 'text-neutral-400'}
                `}
              >
                {displayName || `Selecionar ${label?.toLowerCase()}`}
              </Text>
              <Ionicons
                name="chevron-down"
                size={20}
                className={error ? 'text-red-500' : 'text-neutral-400'}
              />
            </Pressable>

            {error && <Text className="mt-1 text-xs text-red-500">{error.message}</Text>}

            <Modal
              visible={modalVisible}
              transparent
              animationType="fade"
              onRequestClose={closeModal}
            >
              <SafeAreaView className="flex-1 bg-black/50">
                {/* O cartão encolhe para caber acima do teclado: a busca abre
                    focada e os resultados precisam continuar à vista. */}
                <KeyboardAvoidingView behavior="padding" className="flex-1 justify-center">
                  <View className="m-5 mx-auto max-h-[90%] w-[90%] overflow-hidden rounded-2xl bg-card shadow-lg">
                    <View className="flex-row items-center justify-between border-b border-border bg-card p-4">
                      <Text className="font-bold text-lg text-card-foreground">
                        {viewMode === 'search' ? modalTitle : 'Criar Novo Cliente'}
                      </Text>
                      <Pressable onPress={closeModal}>
                        <Ionicons name="close-circle" size={28} className="text-neutral-400" />
                      </Pressable>
                    </View>

                    {allowCreation && (
                      <View className="flex-row gap-2 bg-card px-4 pb-4 pt-2">
                        <Pressable
                          onPress={() => setViewMode('search')}
                          testID="client-modal-tab-buscar"
                          className={`flex-1 items-center justify-center rounded-lg py-3 ${
                            viewMode === 'search' ? 'bg-primary' : 'bg-slate-200/20'
                          }`}
                        >
                          <View className="flex-row items-center gap-2">
                            <Ionicons
                              name="search-outline"
                              size={18}
                              color={viewMode === 'search' ? '#fff' : th('#A1A1AA')}
                            />
                            <Text className={`text-center font-bold text-sm text-card-foreground`}>
                              Buscar
                            </Text>
                          </View>
                        </Pressable>
                        <Pressable
                          onPress={() => {
                            setViewMode('create');
                            resetCreateForm();
                          }}
                          testID="client-modal-tab-criar-novo"
                          className={`flex-1 items-center justify-center rounded-lg py-3 ${
                            viewMode === 'create' ? 'bg-primary' : 'bg-slate-200/20'
                          }`}
                        >
                          <View className="flex-row items-center gap-2">
                            <Ionicons
                              name="person-add-outline"
                              size={18}
                              color={viewMode === 'create' ? '#fff' : th('#A1A1AA')}
                            />
                            <Text className={`text-center font-bold text-sm text-card-foreground`}>
                              Criar Novo
                            </Text>
                          </View>
                        </Pressable>
                      </View>
                    )}

                    {viewMode === 'search' && (
                      <>
                        {/* Search Bar */}
                        <View className="bg-card px-4 pb-2 pt-4">
                          <View className="relative">
                            <View className="absolute left-3.5 top-3.5 z-10 justify-center">
                              <Ionicons
                                name="search-outline"
                                size={20}
                                className="text-neutral-400"
                              />
                            </View>
                            <TextInput
                              value={searchQuery}
                              onChangeText={setSearchQuery}
                              placeholder="Digite para buscar..."
                              placeholderTextColor="#A1A1AA"
                              autoFocus
                              testID="client-modal-search-input"
                              className="rounded-xl border border-neutral-200 bg-card py-3 pl-11 pr-4 text-base text-foreground"
                            />
                          </View>
                        </View>

                        {isLoading && (
                          <View className="bg-card py-8">
                            <ActivityIndicator size="large" className="text-primary" />
                          </View>
                        )}

                        {!isLoading && results.length === 0 && debouncedSearchQuery.length > 1 && (
                          <View className="bg-card py-8">
                            <Text className="text-center text-sm text-gray-500">
                              Nenhum resultado encontrado.
                            </Text>
                          </View>
                        )}

                        {!isLoading && debouncedSearchQuery.length < 2 && (
                          <View className="bg-card py-8">
                            <Text className="text-center text-sm text-card-foreground">
                              Digite ao menos 2 caracteres para buscar
                            </Text>
                          </View>
                        )}

                        <FlatList
                          data={results}
                          keyExtractor={(item) => valueAccessor(item)}
                          contentContainerClassName="px-4 gap-3 pb-4 pt-2"
                          className="shrink bg-card"
                          keyboardShouldPersistTaps="handled"
                          renderItem={({ item }) => (
                            <Pressable
                              onPress={() => handleSelect(item)}
                              className="rounded-lg border border-border bg-background px-5 py-4 active:bg-muted"
                            >
                              {renderListItem(item)}
                            </Pressable>
                          )}
                        />
                      </>
                    )}

                    {viewMode === 'create' && (
                      <ScrollView
                        className="max-h-[60vh] shrink bg-card"
                        keyboardShouldPersistTaps="handled"
                      >
                        <View className="gap-4 px-4 pb-4">
                          <SimpleInput
                            label="Nome Completo"
                            icon="person-outline"
                            placeholder="João Silva"
                            value={newClientData.username}
                            onChangeText={(t) => handleCreateFormChange('username', t)}
                          />
                          <SimpleInput
                            label="E-mail (opcional)"
                            icon="mail-outline"
                            placeholder="joao@email.com"
                            value={newClientData.email}
                            onChangeText={(t) => handleCreateFormChange('email', t)}
                            keyboardType="email-address"
                            autoCapitalize="none"
                          />
                          <SimpleInput
                            label="Telefone"
                            icon="call-outline"
                            placeholder="(11) 98765-4321"
                            value={newClientData.phone}
                            onChangeText={(t) => handleCreateFormChange('phone', t)}
                            keyboardType="phone-pad"
                            maskType="phone"
                          />

                          {createError && (
                            <View className="rounded-lg border border-red-200 bg-red-50 p-4">
                              <Text className="text-center font-medium text-xs text-red-600">
                                {createError}
                              </Text>
                            </View>
                          )}

                          <Pressable
                            onPress={handleCreateNewClient}
                            disabled={isCreating}
                            testID="client-modal-save-btn"
                            className={`mt-2 items-center justify-center rounded-xl bg-primary py-3.5 shadow-lg shadow-orange-500/30 ${
                              isCreating ? 'opacity-70' : 'active:bg-orange-600'
                            }`}
                          >
                            {isCreating ? (
                              <ActivityIndicator color="#fff" />
                            ) : (
                              <Text className="font-bold text-base tracking-wide text-primary-foreground">
                                SALVAR E SELECIONAR CLIENTE
                              </Text>
                            )}
                          </Pressable>
                        </View>
                      </ScrollView>
                    )}
                  </View>
                </KeyboardAvoidingView>
              </SafeAreaView>
            </Modal>
          </View>
        );
      }}
    />
  );
}

type SimpleInputProps = {
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  maskType?: 'phone';
} & React.ComponentProps<typeof TextInput>;

function formatBrazilianPhoneFromAnyInput(input: string | undefined): string {
  if (!input) return '';

  // Mantém apenas dígitos
  let digits = input.replace(/\D/g, '');

  // Se vier com DDI do Brasil (55) e tiver mais de 11 dígitos,
  // removemos o prefixo para manter apenas DDD + número local.
  if (digits.length > 11 && digits.startsWith('55')) {
    digits = digits.slice(digits.length - 11);
  }

  // Garante no máximo 11 dígitos (DDD + 9 dígitos)
  if (digits.length > 11) {
    digits = digits.slice(digits.length - 11);
  }

  // Formata como telefone BR
  const ddd = digits.slice(0, 2);
  const firstPart = digits.slice(2, digits.length <= 10 ? 6 : 7);
  const lastPart = digits.slice(digits.length <= 10 ? 6 : 7);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 6) {
    return `(${ddd}) ${firstPart}`;
  }

  return `(${ddd}) ${firstPart}-${lastPart}`;
}

function SimpleInput({ label, icon, value, onChangeText, maskType, ...props }: SimpleInputProps) {
  if (maskType === 'phone') {
    const handlePhoneChange = (text: string) => {
      const formatted = formatBrazilianPhoneFromAnyInput(text);
      onChangeText?.(formatted);
    };

    return (
      <View className="gap-1">
        <Text className="mb-1 font-semibold text-sm text-card-foreground">{label}</Text>
        <View className="relative">
          <View className="absolute left-3.5 top-3 z-10 justify-center">
            <Ionicons name={icon} size={20} className="text-card-foreground" />
          </View>
          <TextInput
            value={value}
            onChangeText={handlePhoneChange}
            placeholderTextColor="#9CA3AF"
            className="rounded-xl border border-neutral-200 bg-card px-4 py-3 pl-11 text-base text-foreground"
            keyboardType="phone-pad"
            {...props}
          />
        </View>
      </View>
    );
  }

  return (
    <View className="gap-1">
      <Text className="mb-1 font-semibold text-sm text-card-foreground">{label}</Text>
      <View className="relative">
        <View className="absolute left-3.5 top-3 z-10 justify-center">
          <Ionicons name={icon} size={20} className="text-card-foreground" />
        </View>
        <TextInput
          value={value}
          onChangeText={onChangeText}
          placeholderTextColor="#9CA3AF"
          className="rounded-xl border border-neutral-200 bg-card px-4 py-3 pl-11 text-base text-foreground"
          {...props}
        />
      </View>
    </View>
  );
}
